import React from 'react';
import { motion } from 'framer-motion';
import { Book } from 'lucide-react';
const TOMOS = [{
  title: 'TOMO 1: Diagnóstico del Sueño',
  subtitle: 'Descubrí por qué tu bebé no está durmiendo bien y qué lo está saboteando.',
  resolves: 'Antes de cambiar cualquier hábito, necesitás entender el problema de raíz. Este tomo te explica cómo funciona el sueño de un bebé, qué son las asociaciones de sueño, por qué el sobrecansancio sabotea todo y cómo hacer tu propio diagnóstico en menos de 10 minutos.',
  after: 'Identificar exactamente qué está causando los problemas de sueño de tu bebé específico.'
}, {
  title: 'TOMO 2: Ventanas Perfectas',
  subtitle: 'La guía por edad para saber exactamente cuándo dormirlo y evitar el sobrecansancio.',
  resolves: 'El momento en que intentás dormir a tu bebé importa tanto como el método que usás. Este tomo te da la tabla exacta de ventanas de sueño de 4 a 24 meses, las señales tempranas de cansancio que casi nadie ve y los horarios ideales del día según la edad.',
  after: 'Nunca más adivinar cuándo es el momento correcto para dormirlo.'
}, {
  title: 'TOMO 3: Ritual de Noche',
  subtitle: 'La rutina exacta para bajar revoluciones y preparar un dormido mucho más fácil.',
  resolves: 'El cerebro del bebé aprende por repetición. Este tomo te da la estructura completa del ritual de noche en 6 pasos, cómo usar la luz, el sonido y la temperatura a tu favor y cómo adaptar el ritual según la edad del bebé.',
  after: 'Tener una rutina concreta que funcione cada noche sin improvisar.'
}, {
  title: 'TOMO 4: Pase a la Cuna',
  subtitle: 'El protocolo para pasar de brazos a cuna sin arruinar todo en el último segundo.',
  resolves: 'El momento en que el bebé se despierta exactamente cuando lo tocás la cuna. Este tomo te explica por qué pasa eso, cuál es el estado ideal para el traslado y te da el protocolo completo de 8 pasos para hacer el pase sin despertar al bebé.',
  after: 'Transferir a la cuna con mucha más consistencia y menos intentos fallidos.'
}, {
  title: 'TOMO 5: Rescate Nocturno',
  subtitle: 'Qué hacer cuando se despierta de noche y cómo empezar a cortar el ciclo.',
  resolves: 'Los despertares a la 1AM, 3AM y 5AM tienen causas diferentes y necesitan respuestas diferentes. Este tomo te da un protocolo específico para cada hora y un programa de 4 semanas para reducir los despertares de forma gradual.',
  after: 'Responder a cada despertar con protocolo claro en vez de improvisar agotada a las 3AM.'
}, {
  title: 'TOMO 6: Plan 7 Noches',
  subtitle: 'La hoja de ruta completa para aplicar el método y empezar a ver cambios reales.',
  resolves: 'El conocimiento sin implementación no cambia nada. Este tomo te da el plan noche por noche con expectativas reales, herramientas para medir el avance y protocolos de ajuste cuando algo no funciona como esperabas.',
  after: 'Empezar esta misma noche con un plan claro y saber exactamente qué esperar cada día.'
}];
const BONUSES = [{
  title: 'BONUS 1: Detector de Bloqueos del Sueño',
  value: '$15.000',
  desc: 'Tu herramienta de diagnóstico rápido para cualquier noche que algo falle. Funciona como un árbol de decisiones: respondés 7 preguntas y en menos de 5 minutos sabés exactamente qué salió mal y qué ajustar. Cubre los 7 bloqueos más comunes: sobrecansancio, ventana incorrecta, sobreestimulación, ritual inconsistente, causa física, regresión y cambio de ambiente.',
  img: 'https://images.unsplash.com/photo-1553729459-afe8f2e2ed65?w=400&q=80'
}, {
  title: 'BONUS 2: Protocolo Madrugada',
  value: '$15.000',
  desc: 'Tu guía de acción inmediata para usar en el momento exacto del despertar nocturno. Incluye el protocolo específico para la 1AM, las 3AM y las 5AM, la escala de intervención de 5 niveles y las tarjetas de protocolo para tener al lado de la cama. Diseñado para funcionar cuando tu cerebro no está al 100%.',
  img: 'https://images.unsplash.com/photo-1576633587382-13ddf37b1fc1?w=400&q=80'
}, {
  title: 'BONUS 3: Traductor de Llanto y Señales',
  value: '$15.000',
  desc: 'Aprendé a leer lo que tu bebé te dice antes de que llegue al llanto. Incluye el mapa completo de señales de sueño por edad, el traductor de los 5 tipos de llanto, la guía para diferenciar hambre de sueño y las señales de sobrecarga sensorial.',
  img: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&q=80'
}];
export function IncludesSection() {
  return <section className="bg-white py-24 px-4" data-id="element-148">
      <div className="max-w-5xl mx-auto" data-id="element-149">
        <div className="text-center mb-16" data-id="element-150">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-151">
            Todo lo que recibís cuando elegís el Método Noches de Paz™
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-xl text-navy/80 max-w-3xl mx-auto" data-id="element-152">
            Cuando accedés hoy, recibís un sistema completo de 9 PDFs diseñados
            para acompañarte en cada etapa del proceso. Cada material tiene un
            propósito específico y juntos forman un sistema integrado que cubre
            desde el diagnóstico hasta la implementación completa.
          </motion.p>
        </div>

        <h3 className="text-2xl font-bold text-center text-navy mb-10" data-id="element-153">
          LOS 6 TOMOS PRINCIPALES
        </h3>

        <div className="grid md:grid-cols-2 gap-6 mb-20" data-id="element-154">
          {TOMOS.map((tomo, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} className="bg-warm-white p-8 rounded-2xl shadow-soft border border-soft-gray" data-id="element-155">
              <h4 className="text-xl font-bold text-navy mb-2 flex items-center gap-2" data-id="element-156">
                <Book className="text-sky" size={20} data-id="element-157" /> {tomo.title}
              </h4>
              <p className="text-sky font-medium mb-4" data-id="element-158">{tomo.subtitle}</p>
              <div className="space-y-4 text-navy/80" data-id="element-159">
                <p data-id="element-160">
                  <strong data-id="element-161">Qué resuelve:</strong> {tomo.resolves}
                </p>
                <p className="bg-white p-3 rounded-lg border border-sky/20" data-id="element-162">
                  <strong data-id="element-163">Lo que vas a poder hacer después de leerlo:</strong>{' '}
                  {tomo.after}
                </p>
              </div>
            </motion.div>)}
        </div>

        <div className="bg-navy-dark text-white rounded-3xl p-8 md:p-12 shadow-premium relative overflow-hidden" data-id="element-164">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gold/10 rounded-full blur-3xl" data-id="element-165"></div>

          <h3 className="text-3xl font-bold text-center mb-12 relative z-10" data-id="element-166">
            LOS 3 BONUS INCLUIDOS
          </h3>

          <div className="grid md:grid-cols-3 gap-8 relative z-10" data-id="element-167">
            {BONUSES.map((bonus, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white/10 p-6 rounded-2xl backdrop-blur-sm border border-white/10 flex flex-col" data-id="element-168">
                <a href="#compra" className="block mb-4 overflow-hidden rounded-xl relative group" data-id="element-169">
                  <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10 shadow-lg" data-id="element-170">
                    GRATIS POR EL DÍA DE HOY
                  </div>
                  <img src={bonus.img} alt={bonus.title} className="w-full aspect-[4/3] object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-171" />
                </a>
                <h4 className="text-xl font-bold text-gold mb-2 flex items-start gap-2" data-id="element-172">
                  <span data-id="element-173">🎁</span> {bonus.title}
                </h4>
                <p className="text-sm text-white/50 mb-4 line-through" data-id="element-174">
                  Valor percibido: {bonus.value}
                </p>
                <p className="text-white/80 text-sm leading-relaxed flex-grow" data-id="element-175">
                  {bonus.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}